import {Component, Input, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {PortvisitUtils} from '../../../refdata/portvisit-utils';
import {
  BezoekschipOrganisation,
  GuaranteeData,
  ModeOfTransport,
  Terminal,
  TransitDeclaration
} from '@portbase/bezoekschip-service-typescriptmodels';
import {map} from "rxjs/operators";
import {lodash, removeItem} from '../../../common/utils';
import {addEmptyGuarantee} from '../../transit.utils';
import moment from 'moment';
import {
  TransitDeclarationSendTriggerInfoComponent
} from "../../transit-declaration-send-trigger-info/transit-declaration-send-trigger-info.component";
import {AppContext} from "../../../app-context";
import {DaysComponent} from "../../../common/duration/days/days.component";

@Component({
    selector: 'app-transit-details',
    templateUrl: './transit-details.component.html',
    styleUrls: ['./transit-details.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TransitDetailsComponent implements OnInit {
  appContext = AppContext;
  refData = PortvisitUtils;
  allTransitGuarantees: GuaranteeData[] = [];
  @Input() declaration: TransitDeclaration;
  @Input() isContainerDeclaration: boolean = false;
  @Input() isTemplate: boolean = false;
  @Input() isGeneric: boolean = false;
  @Input() displaySendDate: boolean = true;

  @ViewChild(TransitDeclarationSendTriggerInfoComponent) sendTriggerInfoComponent: TransitDeclarationSendTriggerInfoComponent;

  ngOnInit(): void {
    this.refData.getGuarantees(this.declaration.declarantShortName)
      .subscribe(values => this.allTransitGuarantees = lodash.map(values, value => value.guaranteeData));
    this.recalculateExpiryDate();

    if (!this.declaration.data?.placeOfLoading && !!this.declaration.data?.dischargeTerminal) {
      this.setTerminal(this.declaration.data?.dischargeTerminal);
    }
  }

  trackByIndex(index: number, obj: any): any {
    return index;
  }

  findTransitParties = term => this.refData.findTransitParties(term, this.declaration.declarantShortName);

  findContainerOperators = term => this.refData.findContainerOperators(term)
    .pipe(map(carriers => lodash.filter(carriers, carrier => carrier && carrier.customsId)));

  transitGuaranteeFormatter = (g: GuaranteeData) => {
    if (g.guaranteeReferenceNumber) {
      const balance: string = g['balance'] && (g['balance'].amount + ' ' + g['balance'].currency.unCode);
      return g.guaranteeReferenceNumber + ' - ' + g.accessCode + (balance ? ' (balance: ' + balance + ')' : '');
    } else if (g.guaranteeReferenceNumber) {
      return g.otherReference;
    } else {
      return g.type.code + " (" + g.type.description + ")";
    }
  }

  addGuarantee = () => {
    addEmptyGuarantee(this.declaration.data);
  };

  removeGuarantee = (guarantee: GuaranteeData) => () => removeItem(this.declaration.data.guarantees, guarantee);

  setTerminal = (terminal: Terminal) => {
    let currentTerminal = this.declaration.data.dischargeTerminal;
    if (!(terminal && terminal.organisationShortName) && currentTerminal && currentTerminal.organisationShortName) {
      this.declaration.data.placeOfLoading = null;
      this.declaration.data.authorizedLocation = null;
    }
    this.declaration.data.dischargeTerminal = terminal;
    if (terminal && terminal.organisationShortName) {
      PortvisitUtils.getOrganisationByShortName(terminal.organisationShortName)
        .subscribe((organisation: BezoekschipOrganisation) => {
          if (terminal && terminal.organisationShortName) {
            let houseNumber = organisation.arrivalLocationHouseNumber;
            if (!houseNumber) {
              let addressParts = organisation.address.split(' ');
              houseNumber = addressParts.length > 0 ? addressParts[addressParts.length - 1] : '';
            }
            let zipCode = organisation.customsZipCode ? organisation.customsZipCode : organisation.zipCode;
            this.declaration.data.placeOfLoading = zipCode + ' ' + houseNumber;
            this.declaration.data.authorizedLocation = this.declaration.data.placeOfLoading;
          }
        });
    }
  }

  setTransitGuarantee = (value: GuaranteeData, index: number) => {
    console.log("value : ", value);
    this.declaration.data.guarantees[index] = value;
  };

  get today(): string {
    return moment().startOf("day").toISOString()
  };

  updateItinerary() {
    if (!this.isItineraryAllowed()) {
      this.declaration.data.itinerary = [];
    }
  }

  isItineraryAllowed() {
    return this.declaration.data.bindingItinerary;
  }

  changeProcedure = () => {
    this.declaration.data.transportTerm = null;
    this.declaration.requestedTransportExpiryDate = undefined;
  }

  recalculateExpiryDate() {
    this.declaration.requestedTransportExpiryDate = moment(this.declaration.status.sent?.date || this.declaration.status.sendDate)
      .add(DaysComponent.getDays(this.declaration.data.transportTerm), 'days').toISOString();
  }

  isEditable = (): boolean => {
    const status = this.declaration.status;

    if ((!status.cancellation && !status.cancellationRequest && !status.release) &&
      (!status.sent || (!status.accept && status.reject))) {
      return this.appContext.isTransitEditor();
    } else {
      return false;
    }
  };

  showModeOfTransportWarning(modeOfTransport: ModeOfTransport): boolean {
    return modeOfTransport === "NOT_SPECIFIED";
  }

  get requestedTransportExpiryDate() {
    const requestedTransportExpiryDate = this.declaration.requestedTransportExpiryDate;

    if (!requestedTransportExpiryDate) {
      return null;
    } else {
      return moment(requestedTransportExpiryDate).format('YYYY-MM-DD');
    }
  }

  get transportExpiryDate() {
    const transportExpiryDate = this.declaration.transportExpiryDate;

    if (!transportExpiryDate) {
      return this.requestedTransportExpiryDate;
    } else {
      return moment(transportExpiryDate).format('YYYY-MM-DD');
    }
  }

  transitOfficesRequired(): boolean {
    let destinationCountry = this.declaration.data?.destinationsOffice?.unCode?.substring(0, 2);
    return this.isTemplate == false &&
      this.declaration.data?.type?.code != 'TIR' &&
      this.declaration.data?.type?.code != 'T2SM' &&
      ['CH', 'GB', 'IS', 'MK', 'NO', 'RS', 'TR', 'UA'].includes(destinationCountry);
  }
}
